import React, {useEffect, useRef, useState} from 'react'
import {
    Button,
    Dialog,
    DialogBackdrop,
    DialogPanel, DialogTitle,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    TransitionChild,
} from '@headlessui/react'
import {
    AdjustmentsHorizontalIcon,
    ArrowRightStartOnRectangleIcon,
    Bars3Icon,
    BeakerIcon,
    BellIcon,
    CalculatorIcon,
    CalendarDaysIcon,
    CalendarIcon,
    ChartBarIcon,
    ChartPieIcon,
    ClipboardDocumentListIcon,
    Cog6ToothIcon,
    ComputerDesktopIcon,
    DocumentChartBarIcon,
    DocumentDuplicateIcon,
    EyeIcon,
    FolderIcon,
    GlobeAmericasIcon,
    GlobeEuropeAfricaIcon,
    HomeIcon,
    MapIcon,
    MoonIcon,
    QuestionMarkCircleIcon,
    RectangleGroupIcon,
    SunIcon,
    TruckIcon,
    UserCircleIcon,
    UserPlusIcon,
    UsersIcon,
    WrenchScrewdriverIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline'
import {ChevronDownIcon, HeartIcon, MagnifyingGlassIcon} from '@heroicons/react/20/solid'
import Home from "./Home";
import logo from "../GROLINKZ_ICON.png"
import {useDarkMode} from "../contexts/darkModeContext";
import {ChatBubbleLeftEllipsisIcon, PlusIcon} from "@heroicons/react/16/solid";
import { ReactComponent as PFC } from '../customIcons/PFC.svg';
import { ReactComponent as MOWER } from '../customIcons/Mower.svg';
import { ReactComponent as SPRAYER } from '../customIcons/Sprayer_V3.svg';
import { ReactComponent as TOTE } from '../customIcons/Tote.svg';
import MapView from "./Map";
import CourseInfo from "./CourseInfo";
import Account from "./Account";
import Chems from "./Chems";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import CyberBackground from "./CyberBackground";
import {useAuth} from "../contexts/authContext";
import {db} from "../firebase/firebase";
import {doSignOut} from "../firebase/auth";
import Sprays from "./Sprays";
import {formatDate} from "./GlobalFunctions";
import Team from "./Team";
import Fleet from "./Fleet";
import FAQ from "./FAQ";
import ChemsDash from "./ChemsDash";
import SpraysDash from "./SpraysDash";
import FleetDash from "./FleetDash";
import TeamDash from "./TeamDash";
import course from "../photos/course.jpg";
import Calendar from "./Calendar";
import Vision from "./Vision";
import JobBoard from "./JobBoard";
import SprayBuilder from "./Sprays/SprayBuilder";

const navigation = [
    { name: 'Home', href: 'Home', icon: HomeIcon, disabled: false },
    { name: 'Map', href: 'Map', icon: MapIcon, disabled: false },
    { name: 'Course Info', href: '#', icon: RectangleGroupIcon, disabled: false },
    { name: 'PlantFood Connect', href: '#', icon: PFC, disabled: true },
    { name: 'Account', href: '#', icon: UserCircleIcon, disabled: false },

    { name: 'Product Inventory', href: '#', icon: TOTE, disabled: false },
    { name: 'Spray Calculator', href: '#', icon: SPRAYER, disabled: false },
    { name: 'Project Management', href: '#', icon: WrenchScrewdriverIcon, disabled: true },
    { name: 'Fleet Management', href: '#', icon: MOWER, disabled: false },
    { name: 'Team Management', href: '#', icon: UsersIcon, disabled: false },

    { name: 'Product Dashboard', href: '#', icon: ChartPieIcon, disabled: false },
    { name: 'Spray Dashboard', href: '#', icon: DocumentChartBarIcon, disabled: false },
    { name: 'Project Dashboard', href: '#', icon: ChartBarIcon, disabled: false },
    { name: 'Fleet Dashboard', href: '#', icon: UserPlusIcon, disabled: false },

    { name: 'FAQ', href: '#', icon: QuestionMarkCircleIcon, disabled: false },
]

const teams = [
    { id: 1, name: 'Heroicons', href: '#', initial: 'H', current: false },
    { id: 2, name: 'Tailwind Labs', href: '#', initial: 'T', current: false },
    { id: 3, name: 'Workcation', href: '#', initial: 'W', current: false },
]


export function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const Content = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false)

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const navParam = searchParams.get('nav');
        if (navParam) {
            setSelectedNav(navParam);
        }
    }, [searchParams]);

    const { currentUser } = useAuth();
    const { userLoggedIn } = useAuth();
    const { darkMode, toggleDarkMode } = useDarkMode(); // Get dark mode state and toggler from context

    const [time, setTime] = useState('');
    const [date, setDate] = useState('');

    const [city, setCity] = useState("");
    const [state, setState] = useState("");

    useEffect(() => {
        const fetchWeather = async () => {
            // Get current time
            // const currentTime = new Date().toLocaleTimeString();
            const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            setTime(currentTime);

            // Get current date
            const currentDate = new Date().toLocaleDateString();
            setDate(currentDate);
        };

        fetchWeather();

        const interval = setInterval(() => {
            const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            setTime(currentTime);
        }, 60000); // Update every minute

        return () => clearInterval(interval);
    }, []);

    const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
    const [open, setOpen] = useState(false);


    // const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        const savedState = localStorage.getItem('collapseState');
        return savedState === null ? false : JSON.parse(savedState);
    });

    const toggleCollapse = () => {
        setSidebarCollapsed(prevCollapse => {
            const newCollapse = !prevCollapse;
            localStorage.setItem('collapseState', JSON.stringify(newCollapse));
            return newCollapse;
        });
    };

    const [selectedNav, setSelectedNav] = useState('Home');

    const [currentPath] = useState(window.location.pathname); // Initialize currentPath

    const title = `GROLINKZ`;
    const [animateTitle, setAnimateTitle] = useState(false);

    // State for active section and spinning effect
    const [activeSection, setActiveSection] = useState(null);
    const [textVisible, setTextVisible] = useState(null);
    const timeoutRef = useRef(null);

    const handleMouseEnter = (section) => {
        setActiveSection(section);

        // Clear any existing timeouts before creating a new one
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setTextVisible(section);
        }, 200); // Ensure text appears only after expansion
    };

    const handleMouseLeave = () => {
        // Clear the timeout immediately on leave
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        setTextVisible(null);
        setActiveSection(null);
    };



    const job = `Job Board`;
    const [animateJob, setAnimateJob] = useState(false);

    const cal = `Calendar`;
    const [animateCal, setAnimateCal] = useState(false);

    const vis = `Greens Vision`;
    const [animateVis, setAnimateVis] = useState(false);





    const [profileImage, setProfileImage] = useState('');
    const [name, setName] = useState('');
    const [fullName, setFullName] = useState('');
    const [company, setCompany] = useState('');

    useEffect(() => {

        const fetchUserName = async () => {
            if (currentUser) {
                try {
                    // Check if user data is in localStorage
                    const storedUserData = localStorage.getItem(`userData_${currentUser.uid}`);

                    if (storedUserData) {
                        // Use the stored user data
                        const userData = JSON.parse(storedUserData);
                        setProfileImage(userData.profileImage);
                        setName(userData.fname);
                        setFullName(userData.fname + ' ' + userData.lname);
                        setCompany(userData.company);
                        setCity(userData.city);
                        setState(userData.state);
                    } else {
                        // Fetch user document from Firestore based on the current user's UID
                        const userDoc = await db.collection('Users').doc(currentUser.uid).get();
                        if (userDoc.exists) {
                            // Get the user's name from the document data
                            const userData = userDoc.data();
                            setProfileImage(userData.profileImage);
                            setName(userData.fname);
                            setCompany(userData.company);
                            setCity(userData.city);
                            setState(userData.state);

                            // Save user data to localStorage
                            localStorage.setItem(`userData_${currentUser.uid}`, JSON.stringify(userData));
                        } else {
                            console.log('User document not found');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching user data:', error);
                }
            }
        };

        fetchUserName();

    }, [currentUser]);




    const [isVisible, setIsVisible] = useState(!sidebarCollapsed);

    useEffect(() => {
        let timeout;

        if (!sidebarCollapsed) {
            // Delay showing the element
            timeout = setTimeout(() => setIsVisible(true), 600); // adjust delay (ms) as needed
        } else {
            // Instantly hide
            setIsVisible(false);
        }

        return () => clearTimeout(timeout);
    }, [sidebarCollapsed]);


    const section1 = navigation.slice(0, 5); // Adjust indexes based on your layout
    const section2 = navigation.slice(5, 10);
    const section3 = navigation.slice(10, 14);
    const section4 = navigation.slice(14);

    const renderNavItems = (section, drawer) =>
        section.map((item) => {
            const isSelected = item.name === selectedNav;
            const isDisabled = item.disabled;

            return (
                <li key={item.name}>
                    <Button
                        disabled={isDisabled}
                        onClick={() => setSelectedNav(item.name)}
                        className={classNames(
                            isSelected
                                ? 'bg-green-600/20 text-green-600 rounded-r-md border-l-2 border-l-green-600'
                                : `${isDisabled ? '' : `hover:bg-green-600/30 hover:text-green-600 rounded-r-md hover:border-l-2 hover:border-l-green-600`}`,
                            'group w-full flex items-center gap-x-3 px-2 py-2 text-sm/6',
                        )}
                    >
                        <item.icon
                            aria-hidden="true"
                            className={classNames(
                                isSelected
                                    ? `${darkMode ? 'text-white' : 'text-black'} animate-epicReveal`
                                    : `${isDisabled ? 'text-gray-500' : `text-green-600 ${darkMode ? 'group-hover:text-white' : 'group-hover:text-black'} group-hover:animate-twistLiftDrop`}`,
                                `size-6 shrink-0`,
                            )}
                        />
                        <span className={`${isVisible || drawer ? 'animate-zoomIn' : 'hidden'} ${darkMode ? 'text-white' : 'text-black'} text-xs`}>
                            {item.name}
                        </span>
                    </Button>
                </li>
            );
        });


    const [weatherCollapse, setWeatherCollapse] = useState(false);

    return (
        <>
            <div className={`${darkMode ? 'text-white' : 'text-black'}`}>

                <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
                    <DialogBackdrop
                        transition
                        className="fixed inset-0 bg-gray-500/20 transition-opacity duration-300 ease-linear data-closed:opacity-0"
                    />

                    <div className="fixed inset-0 flex">
                        <DialogPanel
                            transition
                            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-500 ease-in-out data-[closed]:-translate-x-full sm:duration-700"
                        >
                            <TransitionChild>
                                <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                                    {/*<button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">*/}
                                    {/*    <span className="sr-only">Close sidebar</span>*/}
                                    {/*    <XMarkIcon aria-hidden="true" className="size-6 text-white" />*/}
                                    {/*</button>*/}
                                </div>
                            </TransitionChild>
                            {/* Sidebar component, swap this element with another sidebar if you like */}
                            <div className={`flex grow flex-col gap-y-1 overflow-y-auto scrollbar-none ${darkMode ? 'bg-bgDark text-white' : 'bg-white text-black'} px-5 pb-4`}>
                                <div className="flex items-center h-16 shrink-0">
                                    <img
                                        alt="Your Company"
                                        src={logo}
                                        className="h-8 w-auto"
                                    />

                                    <h1
                                        className={`animate-bounceIn text-xl font-bold tracking-widest animate-fade-in`}
                                    >
                                        {title.split('').map((char, idx) => (
                                            <span
                                                key={idx}
                                                className={`inline-block ${
                                                    animateTitle ? 'animate-raiseSpinDrop' : ''
                                                }`}
                                                style={{
                                                    animationDelay: animateTitle ? `${idx * 80}ms` : '0s',
                                                    animationFillMode: 'both',
                                                }}
                                                onAnimationEnd={() => {
                                                    // Only reset after the LAST letter finishes animating
                                                    if (idx === title.length - 1) {
                                                        setTimeout(() => setAnimateTitle(false), 1000); // small delay to ensure it's reset cleanly
                                                    }
                                                }}
                                            >
                                                {char === ' ' ? '\u00A0' : char}
                                            </span>
                                        ))}
                                    </h1>

                                </div>
                                <nav className="flex flex-1 flex-col">
                                    <ul role="list" className="flex flex-1 flex-col gap-y-3">


                                        {/*User*/}
                                        <li>
                                            <ul className={`-mx-1 flex items-center`}>
                                                <div
                                                    className={`bg-green-600 text-white size-8 rounded-md flex items-center justify-center`}>S
                                                </div>
                                                <div className={`ml-3 text-sm line-clamp-1`}>{fullName}</div>
                                            </ul>
                                        </li>

                                        {/*Navigation*/}
                                        <li>
                                            <ul role="list" className="-mx-2 space-y-3">

                                                {/*Main*/}
                                                <div className={`relative text-xs text-center my-3`}>
                                                    <div
                                                        className={`absolute inset-0 flex items-center justify-center`}>
                                                        <div
                                                            className={`${darkMode ? 'bg-bgDark' : 'bg-white'} ${sidebarCollapsed ? 'px-0.5' : 'px-2'} tracking-wider text-small`}>Main
                                                        </div>
                                                    </div>

                                                    <div className={`bg-bgcolorLiteGreen w-full rounded-full h-0.5`}></div>
                                                </div>
                                                <div className={`space-y-0.5`}>{renderNavItems(section1, true)}</div>

                                                {/*Calc*/}
                                                <div className={`relative text-xs text-center my-3`}>
                                                    <div className={`absolute inset-0 flex items-center justify-center`}>
                                                        <div className={`${darkMode ? 'bg-bgDark' : 'bg-white'} ${sidebarCollapsed ? 'px-0.5' : 'px-2'} tracking-wider text-small`}>Calc</div>
                                                    </div>

                                                    <div className={`bg-bgcolorLiteGreen w-full rounded-full h-0.5`}></div>
                                                </div>
                                                <div className={`space-y-0.5`}>{renderNavItems(section2, true)}</div>

                                                {/*Data*/}
                                                <div className={`relative text-xs text-center mt-1 mb-3`}>
                                                    <div className={`absolute inset-0 flex items-center justify-center`}>
                                                        <div className={`${darkMode ? 'bg-bgDark' : 'bg-white'} ${sidebarCollapsed ? 'px-0.5' : 'px-2'} tracking-wider text-small`}>Data</div>
                                                    </div>

                                                    <div className={`bg-bgcolorLiteGreen w-full rounded-full h-0.5`}></div>
                                                </div>
                                                <div className={`space-y-0.5`}>{renderNavItems(section3, true)}</div>

                                                {/*Tools*/}
                                                <div className={`relative text-xs text-center mt-1 mb-3`}>
                                                    <div className={`absolute inset-0 flex items-center justify-center`}>
                                                        <div className={`${darkMode ? 'bg-bgDark' : 'bg-white'} ${sidebarCollapsed ? 'px-0.5' : 'px-2'} tracking-wider text-small`}>Tools</div>
                                                    </div>

                                                    <div className={`bg-bgcolorLiteGreen w-full rounded-full h-0.5`}></div>
                                                </div>
                                                <div className={`space-y-0.5`}>{renderNavItems(section4, true)}</div>

                                            </ul>
                                        </li>

                                        {/*Team*/}
                                        <li className={`hidden`}>
                                            <div className="text-xs/6 font-semibold text-gray-400">Your teams</div>
                                            <ul role="list" className="-mx-2 mt-2 space-y-1">
                                                {teams.map((team) => (
                                                    <li key={team.name}>
                                                        <a
                                                            href={team.href}
                                                            className={classNames(
                                                                team.current
                                                                    ? 'bg-gray-50 text-indigo-600'
                                                                    : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                                                                'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                                                            )}
                                                        >
                              <span
                                  className={classNames(
                                      team.current
                                          ? 'border-indigo-600 text-indigo-600'
                                          : 'border-gray-200 text-gray-400 group-hover:border-indigo-600 group-hover:text-indigo-600',
                                      'flex size-6 shrink-0 items-center justify-center rounded-lg border bg-white text-[0.625rem] font-medium',
                                  )}
                              >
                                {team.initial}
                              </span>
                                                            <span className="truncate">{team.name}</span>
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </li>

                                        {/*Settings*/}
                                        <li className="hidden mt-auto">
                                            <a
                                                href="#"
                                                className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 hover:text-indigo-600"
                                            >
                                                <Cog6ToothIcon
                                                    aria-hidden="true"
                                                    className="size-6 shrink-0 text-gray-400 group-hover:text-indigo-600"
                                                />
                                                Settings
                                            </a>
                                        </li>


                                    </ul>
                                </nav>
                            </div>
                        </DialogPanel>
                    </div>
                </Dialog>

                {/* Static sidebar for desktop */}
                <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'} transition-all duration-1000 lg:flex-col`}>

                    {/* Sidebar component, swap this element with another sidebar if you like */}
                    <div className={`flex grow flex-col gap-y-1 overflow-y-scroll overflow-x-hidden scrollbar-none border-r ${darkMode ? 'bg-bgDark border-neutral-700' : 'bg-white  border-gray-200'} px-5 pb-4`}>

                        <div className="flex h-16 shrink-0 items-center">
                            <Button
                                onClick={toggleCollapse}
                                onMouseEnter={() => setAnimateTitle(true)}
                                className={`absolute flex items-center top-3 left-2 ${darkMode ? 'bg-bgDark' : 'bg-white'} z-50`}
                            >
                                <img
                                    alt="Your Company"
                                    src={logo}
                                    className="h-8 w-auto"
                                />

                                <h1
                                    className={`${isVisible ? 'animate-bounceIn hidden lg:block' : 'hidden'} text-xl font-bold tracking-widest animate-fade-in`}
                                >
                                    {title.split('').map((char, idx) => (
                                        <span
                                            key={idx}
                                            className={`inline-block ${
                                                animateTitle ? 'animate-raiseSpinDrop' : ''
                                            }`}
                                            style={{
                                                animationDelay: animateTitle ? `${idx * 80}ms` : '0s',
                                                animationFillMode: 'both',
                                            }}
                                            onAnimationEnd={() => {
                                                // Only reset after the LAST letter finishes animating
                                                if (idx === title.length - 1) {
                                                    setTimeout(() => setAnimateTitle(false), 1000); // small delay to ensure it's reset cleanly
                                                }
                                            }}
                                        >
                                    {char === ' ' ? '\u00A0' : char}
                                </span>
                                    ))}
                                </h1>

                            </Button>
                        </div>

                        <nav className="flex flex-1 flex-col">


                            <ul role="list" className="flex flex-1 flex-col gap-y-3">

                                {/*User*/}
                                <li>
                                    <ul className={`-mx-1 flex items-center`}>
                                        <div className={`bg-green-600 text-white size-8 rounded-md flex items-center justify-center`}>S</div>
                                        <div className={`ml-3 text-sm line-clamp-1 ${isVisible ? 'animate-bounceIn' : 'hidden'}`}>{fullName}</div>
                                    </ul>
                                </li>

                                {/*Navigation*/}
                                <li>
                                    <ul role="list" className="-mx-2 space-y-3">

                                        {/*Main*/}
                                        <div className={`relative text-xs text-center my-3`}>
                                            <div className={`absolute inset-0 flex items-center justify-center`}>
                                                <div
                                                    className={`${darkMode ? 'bg-bgDark' : 'bg-white'} ${sidebarCollapsed ? 'px-0.5' : 'px-2'} tracking-wider text-small`}>Main
                                                </div>
                                            </div>

                                            <div className={`bg-bgcolorLiteGreen w-full rounded-full h-0.5`}></div>
                                        </div>
                                        <div className={`space-y-0.5`}>{renderNavItems(section1)}</div>

                                        {/*Calc*/}
                                        <div className={`relative text-xs text-center my-3`}>
                                            <div className={`absolute inset-0 flex items-center justify-center`}>
                                                <div className={`${darkMode ? 'bg-bgDark' : 'bg-white'} ${sidebarCollapsed ? 'px-0.5' : 'px-2'} tracking-wider text-small`}>Calc</div>
                                            </div>

                                            <div className={`bg-bgcolorLiteGreen w-full rounded-full h-0.5`}></div>
                                        </div>
                                        <div className={`space-y-0.5`}>{renderNavItems(section2)}</div>

                                        {/*Data*/}
                                        <div className={`relative text-xs text-center mt-1 mb-3`}>
                                            <div className={`absolute inset-0 flex items-center justify-center`}>
                                                <div className={`${darkMode ? 'bg-bgDark' : 'bg-white'} ${sidebarCollapsed ? 'px-0.5' : 'px-2'} tracking-wider text-small`}>Data</div>
                                            </div>

                                            <div className={`bg-bgcolorLiteGreen w-full rounded-full h-0.5`}></div>
                                        </div>
                                        <div className={`space-y-0.5`}>{renderNavItems(section3)}</div>

                                        {/*Tools*/}
                                        <div className={`relative text-xs text-center mt-1 mb-3`}>
                                            <div className={`absolute inset-0 flex items-center justify-center`}>
                                                <div className={`${darkMode ? 'bg-bgDark' : 'bg-white'} ${sidebarCollapsed ? 'px-0.5' : 'px-2'} tracking-wider text-small`}>Tools</div>
                                            </div>

                                            <div className={`bg-bgcolorLiteGreen w-full rounded-full h-0.5`}></div>
                                        </div>
                                        <div className={`space-y-0.5`}>{renderNavItems(section4)}</div>

                                    </ul>
                                </li>

                                {/*/!*Team*!/*/}
                                {/*<li>*/}
                                {/*    <div className="text-xs/6 font-semibold text-gray-400">Your teams</div>*/}
                                {/*    <ul role="list" className="-mx-2 mt-2 space-y-1">*/}
                                {/*        {teams.map((team) => (*/}
                                {/*            <li key={team.name}>*/}
                                {/*                <a*/}
                                {/*                    href={team.href}*/}
                                {/*                    className={classNames(*/}
                                {/*                        team.current*/}
                                {/*                            ? 'bg-gray-50 text-indigo-600'*/}
                                {/*                            : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',*/}
                                {/*                        'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',*/}
                                {/*                    )}*/}
                                {/*                >*/}
                                {/*                  <span*/}
                                {/*                      className={classNames(*/}
                                {/*                          team.current*/}
                                {/*                              ? 'border-indigo-600 text-indigo-600'*/}
                                {/*                              : 'border-gray-200 text-gray-400 group-hover:border-indigo-600 group-hover:text-indigo-600',*/}
                                {/*                          'flex size-6 shrink-0 items-center justify-center rounded-lg border bg-white text-[0.625rem] font-medium',*/}
                                {/*                      )}*/}
                                {/*                  >*/}
                                {/*                    {team.initial}*/}
                                {/*                  </span>*/}
                                {/*                    <span className="truncate">{team.name}</span>*/}
                                {/*                </a>*/}
                                {/*            </li>*/}
                                {/*        ))}*/}
                                {/*    </ul>*/}
                                {/*</li>*/}

                                {/*/!*Settings*!/*/}
                                {/*<li className="mt-auto">*/}
                                {/*    <a*/}
                                {/*        href="#"*/}
                                {/*        className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 hover:text-indigo-600"*/}
                                {/*    >*/}
                                {/*        <Cog6ToothIcon*/}
                                {/*            aria-hidden="true"*/}
                                {/*            className="size-6 shrink-0 text-gray-400 group-hover:text-indigo-600"*/}
                                {/*        />*/}
                                {/*        Settings*/}
                                {/*    </a>*/}
                                {/*</li>*/}

                            </ul>
                        </nav>
                    </div>

                </div>

                {/*Body*/}
                <div className={`${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'} h-dvh transition-all duration-1000`}>

                    {/*Header*/}
                    <div className={`sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 ${darkMode ? 'bg-bgDark border-neutral-700' : 'bg-white'} shadow-xs sm:gap-x-6 pr-4 sm:pr-6 lg:pr-8`}>
                        <Button type="button" onClick={() => setSidebarOpen(true)} className="-m-2.5 p-2.5 ml-4 text-gray-700 lg:hidden">
                            <span className="sr-only">Open sidebar</span>
                            <Bars3Icon aria-hidden="true" className="size-6" />
                        </Button>

                        {/* Separator */}
                        <div aria-hidden="true" className="h-6 w-px bg-gray-200 lg:hidden" />

                        {/*Content*/}
                        <div className="flex items-center justify-between flex-1 gap-x-4 self-stretch lg:gap-x-6">

                            {/*Weather Section*/}
                            <div className={`w-fit flex items-center justify-center`}>

                                {/*Weather*/}
                                <div className={`hidden lg:block ${weatherCollapse ? 'w-12' : 'w-64'} border hover:scale-105 transition-all duration-1000 ${darkMode ? 'border-neutral-700' : ''} rounded-lg flex flex-col mx-1.5 items-center justify-center`}>

                                    <div className="relative w-full bg-black rounded-lg h-12 overflow-hidden">
                                        <img
                                            src={course}
                                            alt="background"
                                            className="absolute inset-0 opacity-80 w-full h-full object-cover"
                                        />

                                        <div className="relative z-10 flex flex-row items-center gap-3 h-full px-0.5">
                                            {/*WeatherIcon*/}
                                            <Button onClick={() => setWeatherCollapse(!weatherCollapse)} className={``}>
                                                <SunIcon className="size-10 text-yellow-400 hover:animate-heartbeat"/>
                                            </Button>

                                            {/*Temp*/}
                                            <div className={`${weatherCollapse ? 'hidden' : ''} flex flex-col text-white w-full`}>
                                                <div className={`flex flex-row justify-between text-xs`}>
                                                    <div className={`font-semibold text-lg`}>{formatDate(date)}</div>
                                                    <div className={`flex items-center text-sm font-semibold`}>50 <span
                                                        className="ml-0.5 text-xs">°F</span></div>
                                                    <div className={`flex items-center text-sm font-semibold`}>50 <span
                                                        className="ml-0.5 text-xs">%</span></div>
                                                </div>
                                                <div className={`flex flex-row items-center justify-center text-xs`}>
                                                    <span className={``}>{city}, {state}</span>
                                                </div>
                                            </div>

                                        </div>
                                    </div>

                                </div>

                                {/*Time*/}
                                <div className={`pl-4 sm:pl-6 lg:pl-6 hidden lg:block tracking-widest text-lg hover:scale-105 text-center  rounded-lg  shadow-gray-900`}>
                                    {time}
                                </div>

                            </div>

                            {/*Link*/}
                            <div className="hidden flex-1 flex items-center justify-center">

                                {/*Links*/}
                                <div className={`flex items-center space-x-4`}>

                                    {/* Job Board Link */}
                                    <Button
                                        onClick={() => setSelectedNav('Job Board')}
                                          className={`flex items-center p-1 hover:bg-bgDarkGreen ease-in-out hover:scale-105 rounded-full 
                                            ${selectedNav === 'Job Board' ? 'bg-green-600' : darkMode ? 'bg-bgcard border border-neutral-700 border-opacity-50' : 'bg-bgColor border hover:drop-shadow'} 
                                            ${activeSection === 'job' ? 'w-fit' : 'justify-center'}`}
                                          onMouseEnter={() => {
                                              handleMouseEnter('job');
                                              setAnimateJob(true);
                                          }}
                                          onMouseLeave={() => handleMouseLeave('job')}
                                    >
                                        <div className={`w-8 h-8 rounded-full drop-shadow shadow-2xl shadow-black flex items-center justify-center transition-all duration-500 ease-in-out 
                            ${activeSection === 'job' ? 'mr-20 bg-bgLightGreen animate-zoomFlipBounce' : ''}`}>
                                            <ComputerDesktopIcon
                                                className={`w-5 flex-none ${currentPath === '/JobBoard' ? 'text-white animate-epicReveal' : ''}`}/>
                                        </div>
                                        <div className={`tracking-widest text-xs absolute left-10 transition-opacity duration-300 ease-in-out 
                            ${textVisible === 'job' ? 'animate-fadeIn' : 'opacity-0 invisible'}`}>
                                            {/*Job Board*/}

                                            {job.split('').map((char, idx) => (
                                                <span
                                                    key={idx}
                                                    className={`inline-block ${animateJob ? 'animate-raiseSpinDrop' : ''} ${currentPath === '/job' ? 'text-white' : ''}`}
                                                    style={{
                                                        animationDelay: animateJob ? `${idx * 80}ms` : '0s',
                                                        animationFillMode: 'both',
                                                    }}
                                                    onAnimationEnd={() => {
                                                        // Only reset after the LAST letter finishes animating
                                                        if (idx === title.length - 1) {
                                                            setTimeout(() => setAnimateJob(false), 500); // small delay to ensure it's reset cleanly
                                                        }
                                                    }}
                                                >
                                        {char === ' ' ? '\u00A0' : char}
                                    </span>
                                            ))}

                                        </div>
                                    </Button>

                                    {/* Calendar Link */}
                                    <Button
                                        onClick={() => setSelectedNav('Calendar')}
                                        // to="/Calendar"
                                          className={`flex items-center p-1 hover:bg-bgDarkGreen ease-in-out hover:scale-105 rounded-full 
                                            ${selectedNav === 'Calendar' ? 'bg-green-600' : darkMode ? 'bg-bgcard border border-neutral-700 border-opacity-50' : 'bg-bgColor border hover:drop-shadow'} 
                                            ${activeSection === 'calendar' ? 'w-fit' : 'justify-center'}`}
                                          onMouseEnter={() => {
                                              handleMouseEnter('calendar');
                                              setAnimateCal(true);
                                          }}
                                          onMouseLeave={() => handleMouseLeave('calendar')}
                                    >
                                        <div className={`w-8 h-8 rounded-full drop-shadow shadow-2xl shadow-black flex items-center justify-center transition-all duration-500 ease-in-out 
                                            ${activeSection === 'calendar' ? 'mr-20 bg-bgLightGreen animate-zoomFlipBounce' : ''}`}>
                                            <CalendarDaysIcon
                                                className={`w-5 flex-none ${currentPath === '/Calendar' ? 'text-white animate-epicReveal' : ''}`}/>
                                        </div>
                                        <div className={`tracking-widest text-xs absolute left-10 transition-opacity duration-300 ease-in-out 
                                            ${textVisible === 'calendar' ? 'animate-fadeIn' : 'opacity-0 invisible'}`}>
                                            {/*Calendar*/}
                                            {cal.split('').map((char, idx) => (
                                                <span
                                                    key={idx}
                                                    className={`inline-block tracking-widest ${animateCal ? 'animate-raiseSpinDrop' : ''} ${currentPath === '/Calendar' ? 'text-white' : ''}`}
                                                    style={{
                                                        animationDelay: animateCal ? `${idx * 80}ms` : '0s',
                                                        animationFillMode: 'both',
                                                    }}
                                                    onAnimationEnd={() => {
                                                        // Only reset after the LAST letter finishes animating
                                                        if (idx === title.length - 1) {
                                                            setTimeout(() => setAnimateCal(false), 500); // small delay to ensure it's reset cleanly
                                                        }
                                                    }}
                                                >
                                                    {char === ' ' ? '\u00A0' : char}
                                                </span>
                                            ))}
                                        </div>
                                    </Button>

                                    {/* Vision Link */}
                                    <Button
                                        onClick={() => setSelectedNav('Vision')}
                                        className={`flex items-center p-1 hover:bg-bgDarkGreen ease-in-out hover:scale-105 rounded-full 
                                            ${selectedNav === 'Vision' ? 'bg-green-600' : darkMode ? 'bg-bgcard border border-neutral-700 border-opacity-50' : 'bg-bgColor border hover:drop-shadow'} 
                                            ${activeSection === 'vision' ? 'w-fit' : 'justify-center'}`}
                                          onMouseEnter={() => {
                                              handleMouseEnter('vision');
                                              setAnimateVis(true);
                                          }}
                                          onMouseLeave={() => handleMouseLeave('vision')}
                                    >
                                        <div className={`w-8 h-8 rounded-full drop-shadow shadow-2xl shadow-black flex items-center justify-center transition-all duration-500 ease-in-out 
                                ${activeSection === 'vision' ? 'mr-28 bg-bgLightGreen animate-zoomFlipBounce' : ''}`}>
                                            <EyeIcon
                                                className={`w-5 flex-none ${currentPath === '/Vision' ? 'text-white animate-epicReveal' : ''}`}/>
                                        </div>
                                        <div className={`tracking-widest text-xs absolute left-10 transition-opacity duration-300 ease-in-out 
                                ${textVisible === 'vision' ? 'animate-fadeIn' : 'opacity-0 invisible'}`}>
                                            {/*Greens Vision*/}
                                            {vis.split('').map((char, idx) => (
                                                <span
                                                    key={idx}
                                                    className={`inline-block ${animateVis ? 'animate-raiseSpinDrop' : ''} ${currentPath === '/vision' ? 'text-white' : ''}`}
                                                    style={{
                                                        animationDelay: animateVis ? `${idx * 80}ms` : '0s',
                                                        animationFillMode: 'both',
                                                    }}
                                                    onAnimationEnd={() => {
                                                        // Only reset after the LAST letter finishes animating
                                                        if (idx === title.length - 1) {
                                                            setTimeout(() => setAnimateVis(false), 500); // small delay to ensure it's reset cleanly
                                                        }
                                                    }}
                                                >
                                        {char === ' ' ? '\u00A0' : char}
                                    </span>
                                            ))}
                                        </div>
                                    </Button>

                                </div>


                            </div>

                            {/*Profile Img*/}
                            <div className="flex items-center gap-x-4">

                                <Button
                                    onClick={() => setSelectedNav('Job Board')}
                                    type="button" className={`${selectedNav === 'Job Board' ? 'text-green-600 animate-heartBeatSlow' : ''} -m-2.5 p-2.5 text-gray-400 hover:text-gray-500`}
                                >
                                    <span className="sr-only">View notifications</span>
                                    <ComputerDesktopIcon aria-hidden="true" className="size-6"/>
                                </Button>

                                <Button
                                    onClick={() => setSelectedNav('Calendar')}
                                    type="button" className={`${selectedNav === 'Calendar' ? 'text-green-600 animate-heartBeatSlow' : ''} -m-2.5 p-2.5 text-gray-400 hover:text-gray-500`}
                                >
                                    <span className="sr-only">View notifications</span>
                                    <CalendarDaysIcon aria-hidden="true" className="size-6"/>
                                </Button>

                                <Button
                                    onClick={() => setSelectedNav('Vision')}
                                    type="button" className={`hidden ${selectedNav === 'Vision' ? 'text-green-600 animate-heartBeatSlow' : ''} -m-2.5 p-2.5 text-gray-400 hover:text-gray-500`}
                                >
                                    <span className="sr-only">View notifications</span>
                                    <EyeIcon aria-hidden="true" className="size-6"/>
                                </Button>

                                <Button type="button" className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                                    <span className="sr-only">View notifications</span>
                                    <BellIcon aria-hidden="true" className="size-6"/>
                                </Button>

                                {/* Separator */}
                                <div aria-hidden="true" className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200"/>


                                <Link
                                    to={"/Account"}
                                    className={`hover:animate-rubberBand`}
                                >
                                    <img
                                        alt=""
                                        src={profileImage}
                                        className={`size-9 rounded-full border border-gray-500`}
                                    />
                                </Link>

                                <span className="hidden lg:flex flex-col">
                                  <span aria-hidden="true" className="text-xs">{name}</span>
                                  <span aria-hidden="true" className="text-small text-gray-400">{company}</span>
                                </span>


                                <Button className={``}
                                        onClick={() => setOpen(true)}
                                >
                                    <AdjustmentsHorizontalIcon
                                        className={`size-6 text-green-600 hover:text-opacity-50`}/>
                                </Button>


                                <nav>
                                    {userLoggedIn ? (
                                        <Button
                                            onClick={() => setShowLogoutConfirmation(true)}
                                            className={`${darkMode ? "  " : "drop-shadow"} text-sm text-white h-8 rounded-md hover:animate-pulse p-1 flex items-center justify-center shadow-gray-900`}>
                                            <ArrowRightStartOnRectangleIcon className={`w-6 text-red-500`}/>
                                        </Button>
                                    ) : (
                                        <>
                                            <Link className='text-sm text-white underline mx-2'
                                                  to={'/login'}>Login</Link>
                                            <Link className='text-sm text-white underline mx-2' to={'/register'}>Register
                                                New Account</Link>
                                        </>
                                    )}
                                </nav>


                            </div>

                        </div>

                    </div>

                    {/*Main Section*/}
                    <main className={`h-dvh overflow-y-auto shadow-inner ${darkMode ? 'bg-bgpage' : 'bg-bgcolorLight'}`}>

                        <CyberBackground/>

                        {/*<img*/}
                        {/*    src={course}*/}
                        {/*    alt="background"*/}
                        {/*    className="absolute rounded-lg inset-0 w-full h-full object-cover"*/}
                        {/*/>*/}

                        {/*Components*/}
                        <div className="h-full">
                            {selectedNav === 'Home' && <Home/>}
                            {selectedNav === 'Map' && <MapView/>}
                            {selectedNav === 'Course Info' && <CourseInfo/>}
                            {/*{selectedNav === 'PlantFood Connect' && </>}*/}
                            {selectedNav === 'Account' && <Account/>}

                            {selectedNav === 'Product Inventory' && <Chems/>}
                            {selectedNav === 'Spray Calculator' && <Sprays/>}
                            {/*{selectedNav === 'Project Management' && <Chems/>}*/}
                            {selectedNav === 'Fleet Management' && <Fleet/>}
                            {selectedNav === 'Team Management' && <Team/>}

                            {selectedNav === 'Product Dashboard' && <ChemsDash/>}
                            {selectedNav === 'Spray Dashboard' && <SpraysDash/>}
                            {selectedNav === 'Project Dashboard' && <FleetDash/>}
                            {selectedNav === 'Fleet Dashboard' && <TeamDash/>}

                            {selectedNav === 'FAQ' && <FAQ/>}

                            {selectedNav === 'Job Board' && <JobBoard/>}
                            {selectedNav === 'Calendar' && <Calendar/>}
                            {selectedNav === 'Vision' && <Vision/>}

                            {selectedNav === 'Spray Builder' && <SprayBuilder/>}


                        </div>

                    </main>

                </div>


            </div>


            {/*Options Side Bar*/}
            <Dialog open={open} onClose={setOpen} className="relative z-50">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/20 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0"
                />

                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                            <DialogPanel
                                transition
                                className="pointer-events-auto relative w-96 transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
                            >
                                <TransitionChild>
                                    <div
                                        className="absolute left-0 top-0 -ml-8 flex pr-2 pt-4 duration-500 ease-in-out data-[closed]:opacity-0 sm:-ml-10 sm:pr-4">
                                        <button
                                            type="button"
                                            onClick={() => setOpen(false)}
                                            className="relative rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                        >
                                            <span className="absolute -inset-2.5"/>
                                            <span className="sr-only">Close panel</span>
                                            <XMarkIcon aria-hidden="true" className="size-6 text-red-500"/>
                                        </button>
                                    </div>
                                </TransitionChild>

                                <div className={`h-full overflow-y-auto pr-3 py-3`}>
                                    <div
                                        className={`space-y-6 border h-full ${darkMode ? 'bg-bgDark border-neutral-700 text-white' : 'bg-white text-black'} rounded-lg pb-16 p-8`}>

                                        {/*IMG*/}
                                        <div>
                                            <img
                                                alt=""
                                                src="https://images.unsplash.com/photo-1582053433976-25c00369fc93?ixid=MXwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHw%3D&ixlib=rb-1.2.1&auto=format&fit=crop&w=512&q=80"
                                                className="block aspect-[10/7] w-full rounded-lg object-cover"
                                            />
                                            <div className="mt-4 flex items-start justify-between">
                                                <div>
                                                    <h2 className="text-base font-semibold">
                                                        <span className="sr-only">Details for </span>IMG_4985.HEIC
                                                    </h2>
                                                    <p className="text-sm font-medium text-gray-500">3.9 MB</p>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="relative ml-4 flex size-8 items-center justify-center rounded-full bg-white text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                >
                                                    <span className="absolute -inset-1.5"/>
                                                    <HeartIcon aria-hidden="true" className="size-6"/>
                                                    <span className="sr-only">Favorite</span>
                                                </button>
                                            </div>
                                        </div>

                                        {/*Dark Mode Toggle*/}
                                        <div className={`flex items-center`}>
                                            <div
                                                className={`text-sm font-semibold tracking-widest`}>{darkMode ? 'Dark Mode' : 'Light Mode'}:
                                            </div>
                                            <div className="ml-2">
                                                <input
                                                    type="checkbox"
                                                    id="spray"
                                                    name="spray"
                                                    checked={darkMode}
                                                    onChange={toggleDarkMode} // Call the toggle function from context
                                                    className="sr-only"
                                                />
                                                <label htmlFor="spray" className="cursor-pointer">
                                                    <div
                                                        className={`mr-auto ml-auto w-14 h-8 rounded-full relative ${darkMode ? 'bg-bgpage' : 'bg-bgcolorLight drop-shadow shadow-gray-900'}`}>
                                                        <div
                                                            className={`dot absolute left-1 top-1 w-6 h-6 rounded-full transition-transform duration-300 ${darkMode ? 'bg-white translate-x-full' : 'bg-white'}`}>
                                                            {darkMode ? (
                                                                <MoonIcon
                                                                    className="w-4 h-full flex-none text-black mr-auto ml-auto mt-auto mb-auto"
                                                                    aria-hidden="true"
                                                                />
                                                            ) : (
                                                                <SunIcon
                                                                    className="w-4 h-full flex-none text-black mr-auto ml-auto mt-auto mb-auto"
                                                                    aria-hidden="true"
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </label>
                                            </div>
                                        </div>

                                        {/*Information*/}
                                        <div>
                                            <h3 className="font-medium">Information</h3>
                                            <dl className={`mt-2 divide-y ${darkMode ? 'divide-neutral-700 border-neutral-700' : 'divide-gray-200 border-gray-200'} border-b border-t`}>
                                                <div className="flex justify-between py-3 text-sm font-medium">
                                                    <dt className="text-gray-500">Uploaded by</dt>
                                                    <dd className="">Marie Culver</dd>
                                                </div>
                                                <div className="flex justify-between py-3 text-sm font-medium">
                                                    <dt className="text-gray-500">Created</dt>
                                                    <dd className="">June 8, 2020</dd>
                                                </div>
                                                <div className="flex justify-between py-3 text-sm font-medium">
                                                    <dt className="text-gray-500">Last modified</dt>
                                                    <dd className="">June 8, 2020</dd>
                                                </div>
                                                <div className="flex justify-between py-3 text-sm font-medium">
                                                    <dt className="text-gray-500">Dimensions</dt>
                                                    <dd className="">4032 x 3024</dd>
                                                </div>
                                                <div className="flex justify-between py-3 text-sm font-medium">
                                                    <dt className="text-gray-500">Resolution</dt>
                                                    <dd className="">72 x 72</dd>
                                                </div>
                                            </dl>
                                        </div>

                                        {/*Buttons*/}
                                        <div className="flex">
                                            <Button
                                                type="button"
                                                className="flex-1 rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                            >
                                                Download
                                            </Button>
                                            <Button
                                                type="button"
                                                className={`ml-3 flex-1 rounded-md ${darkMode ? 'bg-bgcard' : 'bg-bgcolorLight'} px-3 py-2 text-sm font-semibold shadow-sm hover:bg-opacity-50`}
                                            >
                                                Delete
                                            </Button>
                                        </div>

                                    </div>
                                </div>
                            </DialogPanel>
                        </div>
                    </div>
                </div>
            </Dialog>

            {/*Logout Popup*/}
            <Dialog open={showLogoutConfirmation} onClose={setShowLogoutConfirmation} className="relative z-50">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/50 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                />

                <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <DialogPanel
                            transition
                            className={`relative transform overflow-hidden rounded-lg border ${darkMode ? 'bg-bgDark border-neutral-700 text-white' : 'bg-white text-black'} px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95`}
                        >
                            <div className="sm:flex sm:items-start">
                                <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-600/15 sm:mx-0 sm:size-10">
                                    <ArrowRightStartOnRectangleIcon aria-hidden="true" className="size-6 text-red-600" />
                                </div>
                                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                    <DialogTitle as="h3" className="text-base font-semibold">
                                        Logout
                                    </DialogTitle>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            Are you sure you want to sign out of your account? This will navigate you back to the Login page and you will have to reenter you credentials to continue!
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                <Button
                                    type="button"
                                    onClick={() => {
                                        doSignOut().then(() => {
                                            navigate('/login');
                                        });
                                    }}
                                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                                >
                                    Log out
                                </Button>
                                <Button
                                    type="button"
                                    // data-autofocus
                                    onClick={() => setShowLogoutConfirmation(false)}
                                    className={`mt-3 inline-flex w-full justify-center rounded-md ${darkMode ? 'bg-bgpage' : 'bg-white ring-1 ring-inset ring-gray-300'} px-3 py-2 text-sm font-semibold shadow-sm hover:bg-opacity-50 sm:mt-0 sm:w-auto`}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>




        </>
    )
}

export default Content
